/**
 * Constants used across components
 */

// ============================================
// API & WebSocket URLs
// ============================================
// REST API server runs on port 8080 (router config: rest.port)
// WebSocket server runs on port 8081 (router config: websocket.port)
export const DEFAULT_BASE_URL = "https://router.morpheum.io";
export const DEFAULT_WS_URL = "wss://router.morpheum.io/websocket";
export const DEFAULT_WSS_URL = "wss://router.morpheum.io/websocket";

// Default device ID for WebSocket connections
export const DEFAULT_DEVICE_ID = "ios-device-1234567890";

// ============================================
// Component Default Heights
// ============================================
export const DEFAULT_HEIGHT_BUCKET_COLLECTIONS = "300px";
export const DEFAULT_HEIGHT_BUCKET_POSITIONS = "300px";
export const DEFAULT_HEIGHT_PENDING_ORDERS = "270px";
export const DEFAULT_HEIGHT_ORDERBOOK = "630px";

// ============================================
// Refresh Intervals (in milliseconds)
// ============================================
export const DEFAULT_REFRESH_INTERVAL = 5000; // 5 seconds

// ============================================
// Market & Trading Defaults
// ============================================
export const DEFAULT_MARKET_ID = "1";
export const DEFAULT_LIMIT = 100;
export const DEFAULT_MAX_DEPTH = 20;

// ============================================
// Mnemonic Defaults
// ============================================
export const DEFAULT_MNEMONIC =
  "legal winner thank year wave sausage worth useful legal winner thank yellow";

// ============================================
// Bucket Equity Test (SDK Test Page)
// ============================================
/** Destination address for bank transfer test (spot wallet → external wallet). */
export const BUCKET_EQUITY_TEST_TRANSFER_TO_ADDRESS =
  "0xe2308fa78086C41A5c7aCfa3cb2EF1F9108679b0";
/** Test amount in USDC for bucket operations (100 or 520). Capped at 50000. */
export const BUCKET_EQUITY_TEST_AMOUNT_USDC = 100;
export const BUCKET_EQUITY_TEST_AMOUNT_CAP_USDC = 50000;
/** Amount for bank transfer to external wallet (small test amount). */
export const BUCKET_EQUITY_TEST_BANK_TRANSFER_AMOUNT_USDC = 1;

// ============================================
// Leverage Settings
// ============================================
export const LEVERAGE_MIN = 1;
export const LEVERAGE_MAX = 100;
export const LEVERAGE_DEFAULT = 1.0;

// ============================================
// Size & Percentage Settings
// ============================================
export const SIZE_PERCENT_MIN = 0;
export const SIZE_PERCENT_MAX = 100;
export const SIZE_PERCENT_STEP = 1;

// ============================================
// Price Input Settings
// ============================================
export const PRICE_STEP = 0.01;
export const PRICE_MIN = 0;

// ============================================
// TradingView Widget Defaults
// ============================================
export const TRADINGVIEW_DEFAULT_SYMBOL = "BINANCE:BTCUSDT";
export const TRADINGVIEW_DEFAULT_INTERVAL = "5";
export const TRADINGVIEW_SCRIPT_URL = "https://s3.tradingview.com/tv.js";

// ============================================
// Address & Index Settings
// ============================================
export const ADDRESS_INDEX_MIN = 0;
export const ADDRESS_INDEX_MAX = 1000;

// ============================================
// Message & Input Limits
// ============================================
export const MESSAGE_LENGTH_MIN = 0;
export const MESSAGE_LENGTH_MAX = 1000;

// ============================================
// Agent Delegation Settings
// ============================================
export const DELEGATION_DAYS_MIN = 1;
export const DELEGATION_DAYS_MAX = 365;

// ============================================
// Display Format Settings
// ============================================
export const CURRENCY_MIN_FRACTION_DIGITS = 2;
export const CURRENCY_MAX_FRACTION_DIGITS = 2;
export const SIZE_DISPLAY_DECIMALS = 3;

// ============================================
// Sort Directions
// ============================================
export const SORT_DIRECTION_ASC = "asc" as const;
export const SORT_DIRECTION_DESC = "desc" as const;

// ============================================
// Order Types
// ============================================
export const ORDER_TYPE_MARKET = "market" as const;
export const ORDER_TYPE_LIMIT = "limit" as const;

// Prefixed order type constants (as they appear in API responses)
export const ORDER_TYPE_PREFIXED_MARKET = "ORDER_TYPE_MARKET" as const;
export const ORDER_TYPE_PREFIXED_LIMIT = "ORDER_TYPE_LIMIT" as const;
export const ORDER_TYPE_PREFIX = "ORDER_TYPE_" as const;

// ============================================
// Margin Modes
// ============================================
export const MARGIN_MODE_CROSS = "CROSS" as const;
export const MARGIN_MODE_ISOLATED = "ISOLATED" as const;

// ============================================
// Position Modes
// ============================================
export const POSITION_MODE_ONE_WAY = "ONE_WAY" as const;
export const POSITION_MODE_HEDGE = "HEDGE" as const;

// ============================================
// Order Sides
// ============================================
export const ORDER_SIDE_BUY = "buy" as const;
export const ORDER_SIDE_SELL = "sell" as const;
export const ORDER_SIDE_LONG = "LONG" as const;
export const ORDER_SIDE_SHORT = "SHORT" as const;

// ============================================
// Position Sort Fields
// ============================================
export const POSITION_SORT_FIELD_SYMBOL = "symbol" as const;
export const POSITION_SORT_FIELD_LEVERAGE = "leverage" as const;
export const POSITION_SORT_FIELD_SIZE = "size" as const;
export const POSITION_SORT_FIELD_LIQUIDATION_PRICE =
  "liquidationPrice" as const;
export const POSITION_SORT_FIELD_UNREALIZED_PNL = "unrealizedPnL" as const;
export const POSITION_SORT_FIELD_ENTRY_PRICE = "entryPrice" as const;
export const POSITION_SORT_FIELD_MARGIN = "margin" as const;

// ============================================
// Position View Modes
// ============================================
export const POSITION_VIEW_MODE_ALL = "all" as const;
export const POSITION_VIEW_MODE_BY_BUCKET = "byBucket" as const;

// ============================================
// Position Display Constants
// ============================================
export const POSITION_GRID_TEMPLATE_COLUMNS =
  "1.5fr 1.2fr 1fr 1fr 1fr 1.2fr 1.2fr 1fr";
export const POSITION_PNL_COLOR_POSITIVE = "#51cf66";
export const POSITION_PNL_COLOR_NEGATIVE = "#ff6b6b";
export const POSITION_LOADING_MESSAGE = "Loading positions...";
export const POSITION_EMPTY_MESSAGE = "No open positions";
export const POSITION_TITLE_PREFIX = "POSITIONS";
export const POSITION_TAB_LABEL_ALL = "All";
export const POSITION_TAB_LABEL_BY_BUCKET = "By Bucket";
export const POSITION_LEVERAGE_FALLBACK = "--";

// ============================================
// Type Definitions
// ============================================
export type PositionSortField =
  | typeof POSITION_SORT_FIELD_SYMBOL
  | typeof POSITION_SORT_FIELD_LEVERAGE
  | typeof POSITION_SORT_FIELD_SIZE
  | typeof POSITION_SORT_FIELD_LIQUIDATION_PRICE
  | typeof POSITION_SORT_FIELD_UNREALIZED_PNL
  | typeof POSITION_SORT_FIELD_ENTRY_PRICE
  | typeof POSITION_SORT_FIELD_MARGIN;

export type PositionSortDirection =
  | typeof SORT_DIRECTION_ASC
  | typeof SORT_DIRECTION_DESC;

export type PositionViewMode =
  | typeof POSITION_VIEW_MODE_ALL
  | typeof POSITION_VIEW_MODE_BY_BUCKET;

// ============================================
// Order Sort Fields
// ============================================
export const ORDER_SORT_FIELD_SYMBOL = "symbol" as const;
export const ORDER_SORT_FIELD_SIDE = "side" as const;
export const ORDER_SORT_FIELD_TYPE = "type" as const;
export const ORDER_SORT_FIELD_QUANTITY = "quantity" as const;
export const ORDER_SORT_FIELD_PRICE = "price" as const;
export const ORDER_SORT_FIELD_FILLED_QUANTITY = "filledQuantity" as const;
export const ORDER_SORT_FIELD_STATUS = "status" as const;

// ============================================
// Order View Modes
// ============================================
export const ORDER_VIEW_MODE_PENDING = "pending" as const;
export const ORDER_VIEW_MODE_HISTORY = "history" as const;

// ============================================
// Order Status Constants (single source of truth)
// ============================================
/** Normalized status (after ORDER_STATUS_ prefix removed) -> display config + pending flag */
export const ORDER_STATUS_CONFIG = {
  PENDING: { isPending: true, short: "P", full: "Pending", color: "#ffc107" },
  OPEN: { isPending: true, short: "O", full: "Open", color: "#17a2b8" },
  ACTIVE: { isPending: true, short: "A", full: "Active", color: "#17a2b8" },
  PARTIAL: { isPending: true, short: "PF", full: "Partially Filled", color: "#ffc107" },
  NEW: { isPending: true, short: "N", full: "New", color: "#ffc107" },
  PARTIALLY_FILLED: { isPending: true, short: "PF", full: "Partially Filled", color: "#ffc107" },
  FILLED: { isPending: false, short: "F", full: "Filled", color: "#28a745" },
  CANCELLED: { isPending: false, short: "C", full: "Canceled", color: "#dc3545" },
  REJECTED: { isPending: false, short: "R", full: "Rejected", color: "#dc3545" },
} as const;

/** Derived: pending statuses for filtering (both normalized and ORDER_STATUS_ prefixed) */
export const ORDER_PENDING_STATUSES: readonly string[] = (
  Object.entries(ORDER_STATUS_CONFIG) as [
    keyof typeof ORDER_STATUS_CONFIG,
    (typeof ORDER_STATUS_CONFIG)[keyof typeof ORDER_STATUS_CONFIG],
  ][]
)
  .filter(([, cfg]) => cfg.isPending)
  .flatMap(([norm]) => [norm, `ORDER_STATUS_${norm}`]);

// Legacy aliases (for backward compat)
export const ORDER_STATUS_PENDING = "ORDER_STATUS_PENDING" as const;
export const ORDER_STATUS_OPEN = "ORDER_STATUS_OPEN" as const;
export const ORDER_STATUS_ACTIVE = "ORDER_STATUS_ACTIVE" as const;
export const ORDER_STATUS_FILLED = "ORDER_STATUS_FILLED" as const;
export const ORDER_STATUS_CANCELLED = "ORDER_STATUS_CANCELLED" as const;
export const ORDER_STATUS_PARTIAL = "ORDER_STATUS_PARTIAL" as const;
export const ORDER_STATUS_REJECTED = "ORDER_STATUS_REJECTED" as const;
export const ORDER_STATUS_PARTIALLY_FILLED = "PARTIALLY_FILLED" as const;

// ============================================
// Order Status Display Colors
// ============================================
export const ORDER_STATUS_COLOR_PENDING = "#ffc107";
export const ORDER_STATUS_COLOR_OPEN = "#17a2b8";
export const ORDER_STATUS_COLOR_FILLED = "#28a745";
export const ORDER_STATUS_COLOR_CANCELED = "#dc3545";
export const ORDER_STATUS_COLOR_REJECTED = "#dc3545";
export const ORDER_STATUS_COLOR_UNKNOWN = "#6c757d";

// ============================================
// Order Side Colors
// ============================================
export const ORDER_SIDE_COLOR_BUY_LONG = "#51cf66";
export const ORDER_SIDE_COLOR_SELL_SHORT = "#ff6b6b";

// ============================================
// Order Display Constants
// ============================================
export const ORDER_GRID_TEMPLATE_COLUMNS =
  "1.5fr 1fr 1fr 1.2fr 1.2fr 1.2fr 1fr 1fr";
export const ORDER_LOADING_MESSAGE = "Loading pending orders...";
export const ORDER_EMPTY_MESSAGE_PENDING = "No pending orders";
export const ORDER_EMPTY_MESSAGE_HISTORY = "No historical orders";
export const ORDER_TITLE_PENDING = "OPEN ORDERS";
export const ORDER_TITLE_HISTORY = "ORDER HISTORY";
export const ORDER_WARNING_WALLET_REQUIRED =
  "Wallet address required to view pending orders";
export const ORDER_TAB_LABEL_PENDING = "Pending";
export const ORDER_TAB_LABEL_HISTORY = "History";
export const ORDER_TYPE_FALLBACK = "--";
export const ORDER_STATUS_UNKNOWN = "Unknown";

// ============================================
// Order Type Display Constants
// ============================================
export const ORDER_TYPE_DISPLAY_LIMIT = "LIMIT";
export const ORDER_TYPE_DISPLAY_MARKET = "MARKET";

// ============================================
// Type Definitions
// ============================================
export type OrderSortField =
  | typeof ORDER_SORT_FIELD_SYMBOL
  | typeof ORDER_SORT_FIELD_SIDE
  | typeof ORDER_SORT_FIELD_TYPE
  | typeof ORDER_SORT_FIELD_QUANTITY
  | typeof ORDER_SORT_FIELD_PRICE
  | typeof ORDER_SORT_FIELD_FILLED_QUANTITY
  | typeof ORDER_SORT_FIELD_STATUS;

export type OrderSortDirection =
  | typeof SORT_DIRECTION_ASC
  | typeof SORT_DIRECTION_DESC;

export type OrderViewMode =
  | typeof ORDER_VIEW_MODE_PENDING
  | typeof ORDER_VIEW_MODE_HISTORY;
